import { useState } from 'react';
import {
  BulkDeleteDialog,
  DeleteDialog,
  ExcelImportDialog,
  ExcelImportErrorDialog,
  ErpDataTableV2
} from '@/components/custom/erp';
import { useCRUD, useExcel, useExcelProcessor, useForms } from '@/hooks';
import { ExcelConfig, ImportBody } from './utils/excel-import';
import type { NhapXuat, NhapXuatInput } from '@/types/schemas';
import { ColumnMapping, ValidationError } from '@/lib/excel';
import { getDataTableColumns } from './cols-definition';
import { ActionBar, FormDialog } from './components';
import { toast } from '@/hooks/use-toast';
import { QUERY_KEYS } from '@/constants';
import { useSearchTable } from './hooks';

const PAGE_SIZE = 50;

export default function LyDoNhapXuatPage() {
  const {
    data,
    totalItems,
    currentPage,
    detail,
    fetchDetail,
    handlePageChange,
    addItem,
    updateItem,
    deleteItem,
    refreshData,
    searchData
  } = useCRUD<NhapXuat, NhapXuatInput>({
    endpoint: QUERY_KEYS.LY_DO_NHAP_XUAT,
    pageSize: PAGE_SIZE
  });

  const {
    showForm,
    showDelete,
    formMode: mode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    showBulkDelete,
    handleCloseBulkDelete,

    clearSelection
  } = useForms<NhapXuat>(fetchDetail);

  const { searchState, handleSearchChange, onPageChange, hasFilterParams } = useSearchTable({
    searchData,
    isFiltered: false,
    handlePageChange: handlePageChange
  });

  const { getUUIDByCode } = useExcel();
  const { processFile } = useExcelProcessor(ExcelConfig);

  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);

  const displayData = hasFilterParams ? searchState.searchResults : data || [];
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;

  const tables = [
    {
      name: '',
      rows: displayData,
      columns: getDataTableColumns()
    }
  ];

  const handleImportBodies = async (bodies: ImportBody[]) => {
    try {
      for (const body of bodies) {
        await addItem(body);
      }
      await refreshData();
      toast({
        description: `Đã nhập ${bodies.length} lý do nhập xuất từ Excel`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Error importing import/export reason from Excel:', error);
      toast({
        description: 'Có lỗi xảy ra khi nhập lý do nhập xuất từ Excel',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const handleImportFromExcel = async (formData: { errors?: string; file: File | null; sheetIndex?: string }) => {
    if (!formData.file) {
      toast({
        description: 'Vui lòng chọn file Excel',
        variant: 'destructive'
      });
      return;
    }

    const maxErrors = parseInt(formData.errors || '200', 10);
    const sheetIndex = parseInt(formData.sheetIndex || '0', 10);

    const result = await processFile({
      file: formData.file,
      sheetIndex,
      maxErrors,
      getUUIDByCode,
      context: {}
    });

    if (result.success && result.data) {
      await handleImportBodies(result.data);
      setIsImportDialogOpen(false);
    } else if (result.errors && result.columnMapping) {
      setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
      setShowErrorDialog(true);
      setIsImportDialogOpen(false);
    }
  };

  const handleErrorDialogClose = () => {
    setShowErrorDialog(false);
    setErrorData(null);
  };

  const handleSubmit = async (data: NhapXuatInput) => {
    try {
      if (mode === 'add') {
        await addItem(data);
      } else if (mode === 'edit' && selectedObj) {
        await updateItem(selectedObj.uuid, data);
      }
      refreshData();
      handleCloseForm();
      clearSelection();
    } catch (err: unknown) {
      console.error('Error submitting form:', err);
    }
  };
  return (
    <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col'>
      <ActionBar
        onAddIconClick={handleAddClick}
        onEditIconClick={() => selectedObj && handleEditClick()}
        onDeleteIconClick={() => selectedObj && handleDeleteClick()}
        onCopyIconClick={() => selectedObj && handleCopyClick()}
        onWatchIconClick={() => selectedObj && handleViewClick()}
        onRefreshClick={refreshData}
        onImportExcel={() => setIsImportDialogOpen(true)}
      />

      <div data-excel-operation-overlay-root className='min-h-0 flex-1 overflow-hidden'>
        <ErpDataTableV2
          tables={tables}
          onRowClick={handleRowClick}
          selectedRowId={selectedRowIndex || undefined}
          totalItems={resolvedTotalItems}
          currentPage={resolvedCurrentPage}
          onPageChange={onPageChange}
          pageSize={PAGE_SIZE}
          onSearchChange={handleSearchChange}
        />
      </div>

      {showForm && (
        <FormDialog
          mode={mode}
          open={showForm}
          onClose={handleCloseForm}
          onAddButtonClick={handleAddClick}
          onEditButtonClick={handleEditClick}
          onCopyButtonClick={handleCopyClick}
          onWatchButtonClick={handleViewClick}
          onDeleteButtonClick={handleDeleteClick}
          onSubmit={handleSubmit}
          initialData={mode === 'add' && !isCopyMode ? undefined : detail}
        />
      )}

      {showDelete && (
        <DeleteDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          deleteObj={deleteItem}
          clearSelection={clearSelection}
        />
      )}

      {isImportDialogOpen && (
        <ExcelImportDialog
          open={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          onSubmit={handleImportFromExcel}
          title='Nhập lý do nhập xuất từ Excel'
        />
      )}

      {showErrorDialog && errorData && (
        <ExcelImportErrorDialog
          open={showErrorDialog}
          onClose={handleErrorDialogClose}
          errors={errorData.errors}
          columnMapping={errorData.columnMapping}
        />
      )}

      {showBulkDelete && (
        <BulkDeleteDialog
          title='Danh mục lý do nhập xuất'
          onClose={handleCloseBulkDelete}
          columns={getDataTableColumns()}
          dataRows={data}
          handleDelete={async (uuid: string) => {
            await deleteItem(uuid);
          }}
          onComplete={refreshData}
        />
      )}
    </div>
  );
}
